import { fetchProjectMeetingFile, useSDK, useStores } from '@littlebird/sdk';
import { observer } from 'mobx-react-lite';
import { useCallback } from 'react';

import { Skeleton, YStack } from '@/components/ui';
import { Editor } from '@/components/ui/editor';
import { resolveFileIdViaDownloadUrl } from '@/components/ui/editor/utils/image';
import { ScrollMask } from '@/components/ui/ScrollMask';
import { Markdown } from '@/components/ui/smoothdown';

import { ProjectItemBreadcrumb } from './projects';
import { useProjectSharedMeetingDetail } from './use-project-shared-meeting-detail';

const EMPTY_DOC = { type: 'doc', content: [{ type: 'paragraph' }] };

export const ProjectSharedMeetingDetailScreen = observer(
  function ProjectSharedMeetingDetailScreen({
    projectId,
    meetingId,
  }: {
    projectId: string;
    meetingId: string;
  }) {
    const sdk = useSDK();
    const { projects } = useStores();
    const project = projects.getById(projectId);
    const { detail, error, isLoading } = useProjectSharedMeetingDetail(
      projectId,
      meetingId
    );

    const resolveMeetingImageFileId = useCallback(
      (fileId: string, onBlobUrl?: (url: string) => void) =>
        resolveFileIdViaDownloadUrl(
          fileId,
          async (id) =>
            (await fetchProjectMeetingFile(sdk, projectId, meetingId, id)).url,
          onBlobUrl
        ),
      [sdk, projectId, meetingId]
    );

    return (
      <YStack className="h-full min-h-0">
        <ProjectItemBreadcrumb project={project} title={detail?.title ?? ''} />
        {isLoading && (
          <YStack className="p-4">
            <Skeleton className="h-24 rounded-lg" />
          </YStack>
        )}
        {!isLoading && error && (
          <YStack className="p-4">
            <p className="text-sm text-foreground-muted">{error}</p>
          </YStack>
        )}
        {!isLoading && !error && detail && (
          <ScrollMask className="flex-1 min-h-0 p-8 md:p-12 lg:px-[20vw]">
            <YStack className="gap-8">
              <YStack className="gap-2">
                <span className="text-xs font-medium text-foreground-muted uppercase tracking-wide">
                  Notes
                </span>
                <Editor
                  id={`shared-meeting-notes-${meetingId}`}
                  initialContent={detail.notes ?? EMPTY_DOC}
                  editable={false}
                  imageUploadBucket={{
                    resolveImageFileId: resolveMeetingImageFileId,
                  }}
                  className="!px-0"
                />
              </YStack>
              {detail.summary && (
                <YStack className="gap-2">
                  <span className="text-xs font-medium text-foreground-muted uppercase tracking-wide">
                    Summary
                  </span>
                  <Markdown
                    id={`shared-meeting-summary-${meetingId}`}
                    content={detail.summary}
                  />
                </YStack>
              )}
              {detail.transcript && (
                <YStack className="gap-2">
                  <span className="text-xs font-medium text-foreground-muted uppercase tracking-wide">
                    Transcript
                  </span>
                  <p className="text-sm text-foreground whitespace-pre-wrap">
                    {detail.transcript}
                  </p>
                </YStack>
              )}
            </YStack>
          </ScrollMask>
        )}
      </YStack>
    );
  }
);
